import { apiClient } from "@/lib/api/client";
import { buildQueryString } from "@/lib/utils/query-string";

// Generic premium CRUD (parameterized by type slug)
function premiumBase(type: string) {
  return `/api/catalog/premiums/${type}/`;
}

export async function listPremiums(type: string, params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${premiumBase(type)}?${query}`);
  return data;
}

export async function getPremium(type: string, id: number) {
  const { data } = await apiClient.get(`${premiumBase(type)}${id}/`);
  return data;
}

export async function createPremium(type: string, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(premiumBase(type), payload);
  return data;
}

export async function updatePremium(type: string, id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${premiumBase(type)}${id}/`, payload);
  return data;
}

export async function deletePremium(type: string, id: number) {
  const { data } = await apiClient.delete(`${premiumBase(type)}${id}/`);
  return data;
}

export async function importPremiums(type: string, file: FormData) {
  const { data } = await apiClient.post(`${premiumBase(type)}import_csv/`, file);
  return data;
}

export async function exportPremiumsCsv(type: string, params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${premiumBase(type)}export_csv/?${query}`, { responseType: "blob" });
  return data as Blob;
}

export async function exportPremiumsJson(type: string, params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${premiumBase(type)}export_json/?${query}`, { responseType: "blob" });
  return data as Blob;
}

export async function addPremiumImages(type: string, id: number, formData: FormData) {
  const { data } = await apiClient.post(`${premiumBase(type)}${id}/add_images/`, formData);
  return data;
}

export async function deletePremiumImage(type: string, id: number, imageId: number) {
  const { data } = await apiClient.delete(`${premiumBase(type)}${id}/delete_image/${imageId}/`);
  return data;
}

export async function movePremiumImage(type: string, id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${premiumBase(type)}${id}/move_image/`, payload);
  return data;
}

// Construction Methods
const CM_BASE = "/api/catalog/premiums/construction_method/";

export async function listConstructionMethods(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${CM_BASE}?${query}`);
  return data;
}

export async function getConstructionMethod(id: number) {
  const { data } = await apiClient.get(`${CM_BASE}${id}/`);
  return data;
}

export async function createConstructionMethod(payload: FormData) {
  const { data } = await apiClient.post(CM_BASE, payload);
  return data;
}

export async function updateConstructionMethod(id: number, payload: FormData) {
  const { data } = await apiClient.patch(`${CM_BASE}${id}/`, payload);
  return data;
}

export async function deleteConstructionMethod(id: number) {
  const { data } = await apiClient.delete(`${CM_BASE}${id}/`);
  return data;
}

export async function deleteConstructionMethodImage(id: number, imageId: number) {
  const { data } = await apiClient.delete(`${CM_BASE}${id}/delete_image/${imageId}/`);
  return data;
}

// Premium Groups
const PG_BASE = "/api/catalog/premium_groups/";

export async function listPremiumGroups(premiumType: string) {
  const { data } = await apiClient.get(`${PG_BASE}?premium_type=${premiumType}`);
  return data;
}

export async function createPremiumGroup(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(PG_BASE, payload);
  return data;
}

export async function updatePremiumGroupIndex(groupId: number, newIndex: number) {
  const { data } = await apiClient.patch(`${PG_BASE}${groupId}/`, { index: newIndex });
  return data;
}

// Validate premiums
export async function validatePremiums(payload: Record<string, unknown>) {
  const { data } = await apiClient.post("/api/catalog/premiums/validate/", payload);
  return data;
}
